/*
 * The auto quoter on the quote form: pick the service, the home's size, how often and the add-ons, and the price works
 * itself out from the company's price list (Settings, Price list). "Put it on the quote" fills the lines, so nobody does
 * the math. The math is in @steward/shared prices.ts, the same rules as the WordPress Steward's quote form. Lawn
 * companies get LawnQuickPrice instead, priced like the GroundsMen website (lawn-prices.ts).
 */
import { Link } from "@tanstack/react-router";
import {
  HOURLY_CENTS,
  LAWN_CONDITIONS,
  LAWN_HEDGES,
  LAWN_OFTEN,
  LAWN_OFTEN_WORDS,
  LAWN_PRICES,
  PRICE_OFTEN,
  PRICE_OFTEN_WORDS,
  hourlyQuoteLines,
  lawnHedgePrice,
  lawnQuoteLines,
  priceHome,
  priceLawn,
  firstVisitOf,
  quickQuoteLines,
  rateOffered,
  type HomeAnswers,
  type LawnAnswers,
  type LawnCondition,
  type LawnHedges,
  type LawnOften,
  type PriceList,
  type PriceOften,
  type QuickQuote,
} from "@steward/shared";
import { useId, useState } from "react";
import { useSettings } from "../api/hooks";
import { useSession } from "../app/session";
import { Choice, FormField, InputCount, InputText } from "../components/fields";
import { money, moneyExact } from "../components/format";
import { LawnMeasure } from "../components/LawnMeasure";
import { Banner, Button, Card, Skeleton, Switch } from "../components/parts";

const num = (s: string) => (s.trim() && Number.isFinite(Number(s)) ? Number(s) : 0);
/** Picks in the Service list past the price list's own services (never a service id, those have no underscore). */
const HOURLY = "_hourly";
const CUSTOM = "_custom";

export function QuickPrice({
  size,
  start,
  useLabel,
  planStart = false,
  onUse,
  onHourly,
}: {
  /** The answers to start from: a job line priced before opens with them, to change a room or how often. */
  start?: HomeAnswers | undefined;
  /** The button that uses the price. */
  useLabel?: string | undefined;
  /** The client was cleaned in the last 30 days: a plan's first visit is a plan visit, no first visit price on top. */
  planStart?: boolean | undefined;
  /** The home's size when it is known already (the new client's address, or the client's first property). */
  size?: { beds: number | null; baths: number | null; rooms?: number | null; sqft: number | null } | undefined;
  onUse: (q: QuickQuote, answers: HomeAnswers) => void;
  /** Given on a new quote: the Service list also has maid service by the hour, and Custom (lines typed by hand). */
  onHourly?: ((q: QuickQuote) => void) | undefined;
}) {
  const { company, can } = useSession();
  const settings = useSettings(company.id);
  const list = settings.data?.settings.prices;
  if (settings.error) return null;
  if (!list)
    return (
      <Card title="Price it fast">
        <Skeleton rows={2} />
      </Card>
    );
  if (!list.services.length && !onHourly)
    return (
      <Card title="Price it fast">
        <p className="muted">
          Type your prices once and quotes price themselves.{" "}
          {can("settings.update") ? (
            <Link to="/quotes/settings" className="link">
              Set up the price list
            </Link>
          ) : (
            "Ask the owner to set up the price list."
          )}
        </p>
      </Card>
    );
  return (
    <QuickPriceForm
      list={list}
      size={size}
      start={start}
      useLabel={useLabel}
      planStart={planStart}
      onUse={onUse}
      onHourly={onHourly}
    />
  );
}

function QuickPriceForm({
  list,
  size,
  start,
  useLabel,
  planStart,
  onUse,
  onHourly,
}: {
  list: PriceList;
  planStart: boolean;
  start: HomeAnswers | undefined;
  useLabel: string | undefined;
  size: { beds: number | null; baths: number | null; rooms?: number | null; sqft: number | null } | undefined;
  onUse: (q: QuickQuote, answers: HomeAnswers) => void;
  onHourly: ((q: QuickQuote) => void) | undefined;
}) {
  const was = (n: number | undefined) => (start && n ? String(n) : "");
  const pickId = useId();
  const [serviceId, setServiceId] = useState(
    start && list.services.some((s) => s.id === start.serviceId) ? start.serviceId : (list.services[0]?.id ?? HOURLY),
  );
  const [sizeBy, setSizeBy] = useState<"rooms" | "sqft">(start?.sizeBy ?? "rooms");
  const [beds, setBeds] = useState(was(start?.beds));
  const [baths, setBaths] = useState(was(start?.baths));
  const [other, setOther] = useState(was(start?.other));
  const [sqft, setSqft] = useState(was(start?.sqft));
  const [often, setOften] = useState<PriceOften>(start?.often ?? "once");
  const [extras, setExtras] = useState<Record<string, boolean | number>>(start?.extras ?? {});
  const [seen, setSeen] = useState("");
  const [typed, setTyped] = useState(Boolean(start));
  // A size typed or loaded elsewhere follows in here, until someone types a size in here themselves.
  const sizeKey = size ? `${String(size.beds)}|${String(size.baths)}|${String(size.rooms)}|${String(size.sqft)}` : "";
  if (size && sizeKey !== seen && !typed) {
    setSeen(sizeKey);
    setBeds(size.beds === null ? "" : String(size.beds));
    setBaths(size.baths === null ? "" : String(size.baths));
    if (size.rooms != null) setOther(String(size.rooms));
    setSqft(size.sqft ? String(size.sqft) : "");
  }
  const own = (set: (v: string) => void) => (v: string) => {
    setTyped(true);
    set(v);
  };
  const service = list.services.find((s) => s.id === serviceId);
  const offered = PRICE_OFTEN.filter((o) => o === "once" || (service?.repeats && rateOffered(list.repeat[o])));
  const pickedOften = offered.includes(often) ? often : "once";
  const answers: HomeAnswers = {
    serviceId,
    sizeBy: sizeBy === "sqft" && service?.perSqftCents !== null ? "sqft" : "rooms",
    beds: num(beds),
    baths: num(baths),
    other: num(other),
    sqft: num(sqft.replace(/,/g, "")),
    often: pickedOften,
    extras,
  };
  const price = priceHome(list, answers);
  const quote = price ? quickQuoteLines(answers, price, planStart) : null;
  const addOns = list.extras.filter((e) => e.only === null || e.only === serviceId);
  const homeFields = (
    <>
      {service?.perSqftCents !== null && service?.perSqftCents !== undefined ? (
        <Choice<"rooms" | "sqft">
          label="Price by"
          value={sizeBy}
          onChange={setSizeBy}
          options={[
            { value: "rooms", label: "Rooms" },
            { value: "sqft", label: "Square feet" },
          ]}
        />
      ) : null}
      <div className="form-grid">
        {answers.sizeBy === "sqft" ? (
          <InputText label="Square feet" inputMode="numeric" value={sqft} onChange={(e) => own(setSqft)(e.target.value)} />
        ) : (
          <>
            <InputCount label="Bedrooms" value={beds} max={20} onChange={own(setBeds)} />
            <InputCount label="Bathrooms" value={baths} max={20} onChange={own(setBaths)} />
            <InputCount
              label="Other rooms"
              value={other}
              max={20}
              description="An office or a game room. The kitchen and living room are counted already."
              onChange={setOther}
            />
          </>
        )}
      </div>
      {offered.length > 1 ? (
        <Choice<PriceOften>
          label="How often"
          value={pickedOften}
          onChange={setOften}
          options={offered.map((o) => ({ value: o, label: PRICE_OFTEN_WORDS[o] }))}
        />
      ) : null}
      {addOns.length ? (
        <div className="field">
          <span className="label">Add-ons on the first visit</span>
          <div className="quick-addons">
            {addOns.map((e) =>
              e.each ? (
                <InputCount
                  key={e.id}
                  label={`${e.name}, ${moneyExact(e.priceCents)} each`}
                  max={200}
                  value={typeof extras[e.id] === "number" && extras[e.id] ? String(extras[e.id]) : ""}
                  onChange={(v) => setExtras({ ...extras, [e.id]: num(v) })}
                />
              ) : (
                <label className="row" key={e.id}>
                  <Switch checked={Boolean(extras[e.id])} onChange={(v) => setExtras({ ...extras, [e.id]: v })} label={e.name} />
                  <span>
                    {e.name} <span className="muted">{money(e.priceCents)}</span>
                  </span>
                </label>
              ),
            )}
          </div>
        </div>
      ) : null}
      {price && quote ? (
        <div className="quick-answer">
          <div>
            <span className="muted small">First visit</span>
            <b className="num">{moneyExact(firstVisitOf(price, planStart))}</b>
            {planStart && price.repeat ? (
              <span className="muted small">Cleaned in the last 30 days, so it starts at the plan price</span>
            ) : price.isMin ? (
              <span className="muted small">The service's lowest price</span>
            ) : null}
          </div>
          {price.repeat ? (
            <div>
              <span className="muted small">Then {price.repeat.label.toLowerCase()}</span>
              <b className="num">{moneyExact(price.repeat.perVisitCents)}</b>
            </div>
          ) : null}
          <Button icon="plus" onClick={() => onUse(quote, answers)}>
            {useLabel ?? "Put it on the quote"}
          </Button>
        </div>
      ) : (
        <Banner>
          {answers.sizeBy === "sqft" ? "Type the square feet to see the price." : "Type the size to see the price."}
        </Banner>
      )}
    </>
  );
  return (
    <Card title="Price it fast">
      <div className="stack">
        <FormField label="Service" htmlFor={pickId}>
          <select id={pickId} className="select" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            {list.services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
            {onHourly ? (
              <>
                <option value={HOURLY}>Maid service, by the hour</option>
                <option value={CUSTOM}>Custom, type your own lines</option>
              </>
            ) : null}
          </select>
        </FormField>
        {serviceId === HOURLY && onHourly ? (
          <HourlyPrice list={list} useLabel={useLabel} onUse={onHourly} />
        ) : serviceId === CUSTOM ? (
          <p className="muted small">Type each line and its price in The work below.</p>
        ) : (
          homeFields
        )}
      </div>
    </Card>
  );
}

/** Maid service by the hour: how many cleaners, how long, how often. The same price every visit. */
function HourlyPrice({
  list,
  useLabel,
  onUse,
}: {
  list: PriceList;
  useLabel: string | undefined;
  onUse: (q: QuickQuote) => void;
}) {
  const [cleaners, setCleaners] = useState("2");
  const [hours, setHours] = useState("3");
  const [often, setOften] = useState<PriceOften>("once");
  const rate = list.hourlyCents ?? HOURLY_CENTS;
  const quote = hourlyQuoteLines(list, { cleaners: num(cleaners), hours: num(hours), often });
  const each = quote?.lines[0]?.unitPriceCents ?? 0;
  return (
    <>
      <div className="form-grid">
        <InputCount label="Cleaners" value={cleaners} min={1} max={20} onChange={setCleaners} />
        <InputCount
          label="Hours"
          value={hours}
          min={1}
          max={24}
          decimal
          description={`${money(rate)} an hour for each cleaner.`}
          onChange={setHours}
        />
      </div>
      <Choice<PriceOften>
        label="How often"
        value={often}
        onChange={setOften}
        options={PRICE_OFTEN.map((o) => ({ value: o, label: PRICE_OFTEN_WORDS[o] }))}
      />
      {quote ? (
        <div className="quick-answer">
          <div>
            <span className="muted small">{often === "once" ? "The visit" : "Each visit"}</span>
            <b className="num">{moneyExact(each)}</b>
          </div>
          <Button icon="plus" onClick={() => onUse(quote)}>
            {useLabel ?? "Put it on the quote"}
          </Button>
        </div>
      ) : (
        <Banner>Type the cleaners and hours to see the price.</Banner>
      )}
    </>
  );
}

const HEDGE_WORDS: Record<LawnHedges, string> = {
  few: "1 to 5 bushes",
  some: "6 to 15",
  many: "16 to 30",
  big: "More than 30 or over 6 ft",
};
const CONDITION_WORDS: Record<LawnCondition, string> = {
  normal: "Under 6 in.",
  tall: "6 to 12 in.",
  overgrown: "Over 12 in.",
};
const NO_PRICE: Record<"no-size" | "too-big" | "photos", string> = {
  "no-size": "Type the grass's square feet, or measure it on the map.",
  "too-big": "Over 1 acre. Price this one by hand after a look.",
  photos: "Overgrown grass is priced from photos. Ask for photos, or quote a schedule.",
};

/**
 * The GroundsMen's auto quoter: the same prices as the website's instant quote (@steward/shared lawn-prices.ts). The
 * grass's size comes from the client's property, the map, or is typed.
 */
export function LawnQuickPrice({
  sqft: known,
  address,
  start,
  useLabel,
  onUse,
}: {
  /** The lawn's size when the property has one. */
  sqft?: number | null | undefined;
  address?: string | null | undefined;
  start?: LawnAnswers | undefined;
  useLabel?: string | undefined;
  onUse: (q: QuickQuote, answers: LawnAnswers) => void;
}) {
  const [sqft, setSqft] = useState(start?.sqft ? String(start.sqft) : "");
  const [seen, setSeen] = useState<number | null>(null);
  const [typed, setTyped] = useState(Boolean(start));
  const [often, setOften] = useState<LawnOften>(start?.often ?? "biweekly");
  const [condition, setCondition] = useState<LawnCondition>(start?.condition ?? "normal");
  const [bagging, setBagging] = useState(start?.bagging ?? false);
  const [hedges, setHedges] = useState<LawnHedges | "none">(start?.hedges ?? "none");
  const [weeds, setWeeds] = useState(start?.weeds ?? false);
  const [measuring, setMeasuring] = useState(false);
  // The property's lawn size follows in here, until someone types or measures one.
  if (known && known !== seen && !typed) {
    setSeen(known);
    setSqft(String(known));
  }
  const answers: LawnAnswers = {
    kind: "lawn",
    sqft: num(sqft.replace(/,/g, "")),
    often,
    condition,
    bagging,
    hedges: hedges === "none" ? null : hedges,
    weeds,
  };
  const price = priceLawn(answers);
  const quote = lawnQuoteLines(price);
  return (
    <Card title="Price it fast">
      <div className="stack">
        <div className="form-grid">
          <InputText
            label="Grass, square feet"
            inputMode="numeric"
            value={sqft}
            description="Just the grass. Leave out the house, driveway and beds."
            onChange={(e) => {
              setTyped(true);
              setSqft(e.target.value);
            }}
          />
          <div className="field">
            <span className="label">&nbsp;</span>
            <Button type="secondary" icon="pin" onClick={() => setMeasuring(true)}>
              Measure on the map
            </Button>
          </div>
        </div>
        <Choice<LawnOften>
          label="How often"
          value={often}
          onChange={setOften}
          options={LAWN_OFTEN.map((o) => ({ value: o, label: LAWN_OFTEN_WORDS[o] }))}
        />
        <Choice<LawnCondition>
          label="Grass height now"
          value={condition}
          onChange={setCondition}
          options={LAWN_CONDITIONS.map((c) => ({ value: c, label: CONDITION_WORDS[c] }))}
        />
        <div className="field">
          <span className="label">Add-ons</span>
          <div className="quick-addons">
            <label className="row">
              <Switch checked={bagging} onChange={setBagging} label="Bag clippings" />
              <span>Bag clippings</span>
            </label>
            <label className="row">
              <Switch checked={weeds} onChange={setWeeds} label="Weed control" />
              <span>
                Weed control <span className="muted">{money(LAWN_PRICES.weeds.price * 100)}</span>
              </span>
            </label>
          </div>
        </div>
        <Choice<LawnHedges | "none">
          label="Hedge and bush trimming"
          value={hedges}
          onChange={setHedges}
          options={[
            { value: "none", label: "None" },
            ...LAWN_HEDGES.map((h) => {
              const p = lawnHedgePrice(h);
              return { value: h, label: p === null ? HEDGE_WORDS[h] : `${HEDGE_WORDS[h]}, ${money(p * 100)}` };
            }),
          ]}
        />
        {price.ok && quote ? (
          <>
            <div className="quick-answer">
              <div>
                <span className="muted small">{price.recurring ? "First visit" : "One time cut"}</span>
                <b className="num">{moneyExact(price.firstVisitCents)}</b>
              </div>
              {price.perVisitCents !== null ? (
                <div>
                  <span className="muted small">Then {LAWN_OFTEN_WORDS[often].toLowerCase()}</span>
                  <b className="num">{moneyExact(price.perVisitCents)}</b>
                </div>
              ) : null}
              <Button icon="plus" onClick={() => onUse(quote, answers)}>
                {useLabel ?? "Put it on the quote"}
              </Button>
            </div>
            {price.photos.length ? (
              <Banner>Ask for photos to price {price.photos.join(" and ").toLowerCase()}. It is not on the quote.</Banner>
            ) : null}
          </>
        ) : (
          <Banner>{price.ok ? NO_PRICE["no-size"] : NO_PRICE[price.reason]}</Banner>
        )}
      </div>
      <LawnMeasure
        open={measuring}
        address={address ?? null}
        onClose={() => setMeasuring(false)}
        onUse={(n) => {
          setTyped(true);
          setSqft(String(n));
          setMeasuring(false);
        }}
      />
    </Card>
  );
}
